import { MISSIONS, getMissionStatus, getMissionProgress } from "../data/vocab";
import { useAuth } from "../context/AuthContext";

export default function MissionMap({ wordStates, onStart, loading }) {
  const { user } = useAuth();
  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "#b0a898" }}>Loading…</div>;

  const totalReviewed = Object.values(wordStates).filter(w => w.lastReviewTime > 0).length;
  const wordsToday = totalReviewed; // simplification
  const firstName = user?.name?.split(" ")[0] || "friend";
  const greeting = getTimeGreeting();

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", position: "relative" }}>
      {/* Decorative SVG background pattern */}
      <div style={{ position: "absolute", inset: 0, zIndex: 0, opacity: 0.5, pointerEvents: "none", overflow: "hidden", borderRadius: 24 }}>
        <svg width="100%" height="100%" viewBox="0 0 720 1200" preserveAspectRatio="xMidYMid slice" style={{ display: "block" }}>
          <defs>
            <pattern id="islamic" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <circle cx="40" cy="40" r="28" fill="none" stroke="#1a8f68" strokeWidth="0.5" opacity="0.15"/>
              <circle cx="40" cy="40" r="14" fill="none" stroke="#1a8f68" strokeWidth="0.5" opacity="0.15"/>
              <path d="M 40 12 L 40 68 M 12 40 L 68 40" stroke="#1a8f68" strokeWidth="0.4" opacity="0.12"/>
              <path d="M 20 20 L 60 60 M 60 20 L 20 60" stroke="#1a8f68" strokeWidth="0.4" opacity="0.12"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#islamic)"/>
        </svg>
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Welcome banner */}
        <div style={{
          background: "linear-gradient(135deg, #1a8f68 0%, #157a58 100%)",
          borderRadius: 22, padding: "26px 30px", marginBottom: 32,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 20, position: "relative", overflow: "hidden",
          boxShadow: "0 8px 30px rgba(26,143,104,0.2)",
        }}>
          {/* Decorative circles */}
          <div style={{ position: "absolute", right: -30, top: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.07)" }} />
          <div style={{ position: "absolute", right: 60, bottom: -50, width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
          {/* Persian script watermark */}
          <div style={{ position: "absolute", right: 24, top: 18, fontSize: 80, color: "rgba(255,255,255,0.08)", fontWeight: 900, lineHeight: 1, fontFamily: "serif" }}>سلام</div>

          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>
              {greeting}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 4, letterSpacing: -0.4 }}>
              Welcome back, {firstName}!
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
              {totalReviewed > 0 ? `You've learned ${totalReviewed} words. Keep going!` : "Ready to start your Persian journey?"}
            </div>
          </div>
        </div>

        {/* Title */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h2 style={{ fontSize: 26, fontWeight: 900, color: "#111", marginBottom: 6, letterSpacing: -0.5 }}>Mission Map</h2>
          <p style={{ fontSize: 13, color: "#b0a898" }}>Complete missions to unlock new words</p>
        </div>

        {/* Mission cards */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {MISSIONS.map((mission, idx) => {
            const status = getMissionStatus(mission, wordStates, idx);
            const prog = getMissionProgress(mission, wordStates);
            const isLocked = status === "locked";
            const isComplete = prog.reviewed >= prog.total;
            const isCurrent = !isLocked && !isComplete;

            return (
              <div key={mission.id} style={{ width: "100%", maxWidth: 480 }}>
                <MissionCard mission={mission} prog={prog} isLocked={isLocked} isComplete={isComplete} isCurrent={isCurrent} onStart={() => onStart(mission)} />
                {idx < MISSIONS.length - 1 && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "8px 0" }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: isComplete ? "#4ec99a" : "#d5cfc5" }} />
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: isComplete ? "#4ec99a" : "#d5cfc5" }} />
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: isComplete ? "#4ec99a" : "#d5cfc5" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MissionIcon({ icon, color = "#1a8f68" }) {
  if (icon === "tulip") return (
    <svg width="22" height="22" viewBox="0 0 22 26" fill="none">
      <line x1="11" y1="25" x2="11" y2="16" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M11 20 Q7 18 6.5 14 Q9 15.5 11 19" fill={color} opacity="0.5"/>
      <path d="M11 16 Q6 12 6 7.5 Q8.5 5.5 11 9 Q13.5 5.5 16 7.5 Q16 12 11 16Z" fill={color}/>
    </svg>
  );
  if (icon === "hand") return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11V6a1 1 0 012 0v5M11 6V4a1 1 0 012 0v7M13 6a1 1 0 012 0v5M7 12V9a1 1 0 012 0v3"/>
      <path d="M7 13c0 3.3 1.8 6 5 7 3 .9 5-1 5-4v-3"/>
    </svg>
  );
  if (icon === "pom") return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <path d="M12 3 Q12 6 12 7"/><path d="M10 6 Q8 5 7 3"/>
      <circle cx="12" cy="14" r="7"/>
      <path d="M9 10 Q10 12 12 11 Q14 12 15 10"/>
      <path d="M8 14 Q10 16 12 15 Q14 16 16 14"/>
    </svg>
  );
  // arch
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <path d="M4 21V11a8 8 0 0116 0v10"/>
      <path d="M4 21h16"/>
      <path d="M9 21v-6a3 3 0 016 0v6"/>
    </svg>
  );
}

function getTimeGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning · صبح بخیر";
  if (h < 18) return "Good afternoon · ظهر بخیر";
  return "Good evening · عصر بخیر";
}

function MissionCard({ mission, prog, isLocked, isComplete, isCurrent, onStart }) {
  return (
    <button disabled={isLocked} onClick={onStart} style={{
      width: "100%",
      background: isCurrent ? "#f4fbf8" : "#fff",
      border: isCurrent ? "2px solid #1a8f68" : "1.5px solid #ede8df",
      borderRadius: 18, padding: "16px 18px",
      cursor: isLocked ? "not-allowed" : "pointer",
      textAlign: "left", fontFamily: "inherit",
      opacity: isLocked ? 0.45 : 1,
      transition: "all 0.2s ease",
      display: "block",
      boxShadow: isCurrent ? "0 4px 16px rgba(26,143,104,0.12)" : "none",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: isLocked ? "#f3f1ec" : "#e8f7f1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {isLocked
            ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.8" strokeLinecap="round"><rect x="6" y="11" width="12" height="9" rx="2"/><path d="M9 11V7a3 3 0 016 0v4"/></svg>
            : <MissionIcon icon={mission.icon} color="#1a8f68" />
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: isLocked ? "#bbb" : "#111" }}>{mission.title}</span>
            {isComplete && <span style={{ fontSize: 10, fontWeight: 700, color: "#1a8f68", background: "#e8f7f1", borderRadius: 20, padding: "2px 9px", letterSpacing: 0.5, textTransform: "uppercase" }}>Done</span>}
            {isCurrent && <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "#1a8f68", borderRadius: 20, padding: "2px 9px", letterSpacing: 0.5, textTransform: "uppercase" }}>Active</span>}
            {isLocked && <span style={{ fontSize: 10, fontWeight: 700, color: "#bbb", background: "#f3f1ec", borderRadius: 20, padding: "2px 9px", letterSpacing: 0.5, textTransform: "uppercase" }}>Locked</span>}
          </div>
          <div style={{ fontSize: 12, color: "#b0a898", marginBottom: isLocked ? 0 : 8 }}>
            {isLocked ? "Complete previous mission to unlock" : mission.subtitle}
          </div>
          {!isLocked && (
            <>
              <div style={{ background: "#ede8df", borderRadius: 99, height: 7, overflow: "hidden" }}>
                <div style={{ width: `${prog.pct}%`, height: "100%", borderRadius: 99, background: "#1a8f68", transition: "width 0.5s ease" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                <span style={{ fontSize: 11, color: "#c0b8ae" }}>{prog.reviewed} / {prog.total} words</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#1a8f68" }}>{prog.pct}%</span>
              </div>
            </>
          )}
        </div>
        {isCurrent && (
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1a8f68", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M3 7h8M7.5 3.5l4 3.5-4 3.5"/></svg>
          </div>
        )}
      </div>
    </button>
  );
}
